// v3/layouts/AppLayout.tsx — S4 Composite app shell
// Navy chassis carries navigation: icon rail (desktop), app bar with the
// section title (phone), bottom nav (phone). The content plane floats inside
// as a rounded card and belongs entirely to data.
// Zero imports from v1/v2 (except the experience toggle).

import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../design-system/utils';
import { toggleTheme } from '../design-system/composites/ThemeToggle';
import { NAVIGATION_ITEMS } from '../config/navigation.config';
import { usePermission } from '../shared/hooks/usePermission';
import { useAuth } from '../shared/hooks/useAuth';
import { useIsMobile } from '../shared/hooks/useIsMobile';
import { useAuthStore } from '../shared/stores/auth-store';
import { Breadcrumbs } from '../shared/components/Breadcrumbs';
import { LocationGuard } from '../shared/components/LocationGuard';
import { RumPageTracker } from '../shared/components/RumPageTracker';
import { switchExperience } from '../../shared/experience-toggle';
import { BottomNav } from './MobileLayout';

function getIcon(name: string): LucideIcon {
  return (Icons as unknown as Record<string, LucideIcon>)[name] ?? Icons.Circle;
}

// ─── Icon rail (desktop) ─────────────────────────────────────────────────────

function Rail() {
  const { canAccessFeature } = usePermission();
  // Rail carries only the primary destinations (same five as the phone bottom
  // nav); tools/settings/session live in the avatar menu (locked decision).
  const items = NAVIGATION_ITEMS.filter((i) => i.bottomNav && canAccessFeature(i.featureId))
    .sort((a, b) => (a.bottomNav ?? 0) - (b.bottomNav ?? 0));

  return (
    <aside className="hidden w-14 shrink-0 flex-col items-center gap-1 pb-2.5 pt-2.5 md:flex">
      <NavLink
        to="/"
        className="mb-2 flex h-8 w-8 items-center justify-center rounded-[9px] bg-primary text-sm font-extrabold text-primary-foreground"
        title="Today"
      >
        P
      </NavLink>
      {items.map((item) => {
        const Icon = getIcon(item.icon);
        return (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            title={item.label}
            className={({ isActive }) =>
              cn(
                'flex h-10 w-10 items-center justify-center rounded-[10px] transition-colors duration-fast',
                isActive
                  ? 'bg-white/[.14] text-white'
                  : 'text-[var(--m-sidebar-fg)] opacity-65 hover:bg-white/[.08] hover:opacity-100',
              )
            }
          >
            <Icon className="h-[19px] w-[19px]" strokeWidth={1.75} />
          </NavLink>
        );
      })}
      {/* The rail's grammar, completed: the product (logo), the places (nav),
          the person (avatar) — identity anchors the bottom corner, menus open
          upward. The desktop plane spends nothing on account chrome. */}
      <div className="mt-auto flex flex-col items-center gap-1.5">
        <WarningBadge onChassis />
        <AvatarMenu onChassis />
      </div>
    </aside>
  );
}

// ─── Warning badge ───────────────────────────────────────────────────────────

function WarningBadge({ onChassis }: { onChassis?: boolean }) {
  const warnings = useAuthStore((s) => s.warnings);
  const [open, setOpen] = useState(false);

  if (warnings.length === 0) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'relative flex h-8 w-8 items-center justify-center rounded-full transition-colors',
          onChassis ? 'hover:bg-white/[.08]' : 'hover:bg-secondary',
        )}
        title={`${warnings.length} warning${warnings.length > 1 ? 's' : ''}`}
      >
        <Icons.AlertTriangle className="h-4.5 w-4.5 text-[var(--ch-attention-fg)]" />
        <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--ch-attention-fg)] text-[9px] font-bold text-white">
          {warnings.length}
        </span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={cn(
              'absolute z-50 w-72 overflow-hidden rounded-[var(--shape-card)] border border-border bg-popover shadow-lg',
              onChassis ? 'bottom-0 left-10' : 'right-0 top-10',
            )}
          >
            <div className="border-b border-border bg-[var(--ch-attention-bg)] px-3 py-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ch-attention-fg)]">Account Warnings</p>
            </div>
            <div className="max-h-60 overflow-y-auto">
              {warnings.map((w) => (
                <div key={w.id} className="flex gap-2 border-b border-border px-3 py-2.5 last:border-b-0">
                  <Icons.AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--ch-attention-fg)]" />
                  <p className="text-sm text-foreground">{w.message}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Avatar menu ─────────────────────────────────────────────────────────────
// Carries the long tail the bottom nav doesn't: tools, settings, session.

function AvatarMenu({ onChassis }: { onChassis?: boolean }) {
  const { user, logout } = useAuth();
  const { canAccessFeature } = usePermission();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!user) return null;

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const itemCls =
    'flex w-full items-center gap-2 px-3 py-2 text-foreground hover:bg-secondary';

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold',
          onChassis
            ? 'bg-white/15 text-white'
            : 'bg-[var(--m-sidebar)] text-white dark:bg-[var(--m-hilite)] dark:text-foreground',
        )}
      >
        {(user.userName?.[0] || 'U').toUpperCase()}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={cn(
              'absolute z-50 w-52 rounded-[var(--shape-card)] border border-border bg-popover py-1 text-sm shadow-lg',
              onChassis ? 'bottom-0 left-10' : 'right-0 top-10',
            )}
          >
            <div className="border-b border-border px-3 py-2">
              <p className="font-medium text-foreground">{user.userName}</p>
              <p className="text-xs text-muted-foreground">
                {user.roleAssigned === false ? (
                  <span className="text-[var(--ch-attention-fg)]">No role assigned</span>
                ) : (
                  user.role?.replace(/_/g, ' ')
                )}
              </p>
            </div>
            {canAccessFeature('emi-calculator') && (
              <button onClick={() => go('/emi-calculator')} className={itemCls}>
                <Icons.Calculator className="h-3.5 w-3.5" /> EMI Calculator
              </button>
            )}
            {canAccessFeature('login-history') && (
              <button onClick={() => go('/login-history')} className={itemCls}>
                <Icons.History className="h-3.5 w-3.5" /> Activity History
              </button>
            )}
            {canAccessFeature('settings') && (
              <button onClick={() => go('/settings')} className={itemCls}>
                <Icons.Settings className="h-3.5 w-3.5" /> Settings
              </button>
            )}
            <button
              onClick={() => { toggleTheme(); setOpen(false); }}
              className={itemCls}
            >
              {document.documentElement.classList.contains('dark') ? (
                <><Icons.Sun className="h-3.5 w-3.5" /> Light mode</>
              ) : (
                <><Icons.Moon className="h-3.5 w-3.5" /> Dark mode</>
              )}
            </button>
            <button
              onClick={() => { setOpen(false); switchExperience('v1'); }}
              className={itemCls}
            >
              <Icons.ArrowLeftRight className="h-3.5 w-3.5" /> Switch to Classic
            </button>
            <button
              onClick={() => { setOpen(false); logout(); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-[var(--ch-danger-fg)] hover:bg-secondary"
            >
              <Icons.LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── In-plane header (phone only) ────────────────────────────────────────────
// Quiet, edge-to-edge: just the account entry point; titles belong to pages.
// On desktop the avatar lives at the bottom of the rail — the plane spends
// no vertical space on account chrome.

function PlaneHeader() {
  return (
    <header className="flex h-11 shrink-0 items-center justify-end gap-1.5 px-3 md:hidden">
      <WarningBadge />
      <AvatarMenu />
    </header>
  );
}

// ─── Layout ──────────────────────────────────────────────────────────────────

export function AppLayout() {
  const isMobile = useIsMobile();

  return (
    <div className="flex h-dvh overflow-hidden" style={{ background: 'var(--m-sidebar)' }}>
      <Rail />
      <div className="flex min-w-0 flex-1 flex-col p-1.5 md:p-2.5 md:pl-1">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[var(--shape-plane)] border border-[var(--plane-bd)] bg-background shadow-[var(--plane-sh)]">
          <PlaneHeader />
          <main className="flex-1 overflow-y-auto">
            <div className="hidden px-4 pb-1 pt-3 md:block">
              <Breadcrumbs />
            </div>
            <div className="animate-fade-in px-3 pb-4 pt-3 md:px-4 md:pt-1">
              <LocationGuard>
                <RumPageTracker />
                <Outlet />
              </LocationGuard>
            </div>
          </main>
        </div>
        {isMobile && <BottomNav />}
      </div>
    </div>
  );
}
